import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { addDays, isFuture, isPast } from "date-fns";
import { Formik, type FormikProps } from "formik";
import * as yup from "yup";

import actions from "actions";
import { getCustomerDetails } from "selectors";
import * as selectors from "selectors";
import { COUNTRIES, STATES, RELATIONSHIPS } from "@constants/index";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useGetTeams } from "hooks/api/useGetTeams";
import useUpdateClientInsurance from "hooks/api/client/useSaveClientInsurance";
import useDisableClientInsurance from "hooks/api/client/useDisableClientInsurance";
import { LoadingScreen } from "components/EventScheduling/EventSchedulingUtils";
import { useGlobalToast } from "components/GlobalToastProvider";
import { formatToPhoneNumber } from "utils/formatFormFields";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import { teamMembersWithClient } from "../Utils/teamMembersWithClient";
import EditUserInfoAlert from "../Utils/EditUserInfoAlert";
import useGetInsurancePlans from "hooks/api/insurance/useGetInsurancePlans";

const validationSchema = yup.object().shape({
  insuranceId: yup.string().required("Please select an insurer"),
  insurancePlanId: yup.string().required("Please select an insurance plan"),
  memberId: yup.string().required("Please enter the Member ID"),
  groupNumber: yup.string().nullable(),
  effectiveDate: yup.date().required("Please select an effective date"),
  expirationDate: yup.date().nullable(),
  isPrimary: yup.boolean(),
  subscriberEmail: yup.string().email("Invalid email").nullable(),
  subscriberDOB: yup.date().nullable(),
  subscriberPhone: yup.string().nullable(),
  subscriberRelationship: yup.string().nullable(),
  subscriberCountry: yup.string().nullable(),
  subscriberState: yup.string().nullable(),
  subscriberCity: yup.string().nullable(),
  subscriberZip: yup.number().nullable(),
  subscriberAddress: yup.string().nullable(),
  subscriberAddress2: yup.string().nullable(),
});

type InsurancePanelEditFormProps = {
  insurance?: Record<string, any>;
  userInsurance?: any;
  primaryActiveExists?: boolean;
  clientId: string;
  setSidePanelContent: (content: React.ReactNode) => void;
  setShowSecondaryAsPrimaryDialog?: (show: boolean) => void;
  setPendingPrimaryInsuranceData?: (data: any) => void;
};

export default function InsurancePanelEditForm({
  insurance,
  userInsurance,
  primaryActiveExists,
  clientId,
  setSidePanelContent,
  setShowSecondaryAsPrimaryDialog,
  setPendingPrimaryInsuranceData,
}: InsurancePanelEditFormProps) {
  const { showToast } = useGlobalToast();
  const dispatch = useDispatch();
  const customerData: any = useSelector(getCustomerDetails);
  const customerDetails = customerData?.details;

  const isAddingInsurance = !insurance?.id;

  const { teams, teamsLoading } = useGetTeams({ clientIds: clientId });
  const teamId = teams?.[0]?.id;

  const { teamMembers, teamMembersLoading } = useGetTeamMembers({
    teamIds: teamId,
    includeTeamInvitees: false,
  });

  const teamMembersWithClientData =
    teamMembers && teamMembers.length > 0
      ? teamMembersWithClient(teamMembers, customerDetails)
      : [];

  const insuranceProvidersList: any[] = useSelector(selectors.getInsuranceProvidersList);

  // Update|Create Insurance Mutation
  const { isMutationInsuranceLoading, mutateAsync } = useUpdateClientInsurance();
  const { mutateAsync: disableInsuranceMutationAsync } = useDisableClientInsurance();

  // Type-safe mutation wrappers
  const updateInsurance = (data: any) => mutateAsync(data);
  const disableInsurance = (data: any) => disableInsuranceMutationAsync(data);

  // Ref to access Formik imperatively (used by the plan-name effect below)
  const formikRef = useRef<FormikProps<any>>(null);

  // Track the selected insurance ID in local state so hooks at this level can react to it.
  // Kept in sync with the Formik field via handleInsuranceCompanyChange.
  const [activeInsuranceId, setActiveInsuranceId] = useState<string | null>(
    () =>
      insuranceProvidersList?.find(({ name }: any) => name === insurance?.insuranceName)?.id ?? null
  );

  const { insurancePlans, fetchStatus } = useGetInsurancePlans({ insuranceid: activeInsuranceId });

  const insurancePlanOptions = insurancePlans
    ? insurancePlans
        ?.filter(
          ({ insurance_id }: any) => parseInt(insurance_id) === parseInt(activeInsuranceId)
        )
        .map(({ id, name }: any) => ({ label: name, value: id }))
    : [];

  // Once plans load, auto-select the plan that matches the existing insurance record.
  useEffect(() => {
    if (insurance?.planName && !formikRef.current?.values.insurancePlanId && insurancePlans.length) {
      const plan = insurancePlans?.find(({ name }: any) => name === insurance?.planName);
      formikRef.current?.setFieldValue("insurancePlanId", plan?.id || "");
    }
  }, [fetchStatus, insurancePlans.length]);

  if (teamsLoading || teamMembersLoading) return <LoadingScreen />;

  const onSubmit = async (values: any) => {
    const isSettingAsPrimary = values.isPrimary && !insurance?.is_primary;

    // Create the actual submit function
    const executeSubmit = async () => {
      try {
        let otherInsuranceUpdates: Promise<any>[] = [];
        if (isSettingAsPrimary) {
          // find all the other insurances that are primary and set them to false
          const otherPrimaryInsurances = userInsurance?.filter((insurance: any) => {
            const expirationDate = new Date(insurance.expiration_date);
            return (
              insurance.is_primary &&
              insurance.id !== values.id &&
              insurance.active &&
              (insurance.expiration_date ? expirationDate > new Date() : true)
            );
          });
          if (otherPrimaryInsurances?.length) {
            otherInsuranceUpdates = [
              ...otherPrimaryInsurances.map((primaryInsurance: any) =>
                updateInsurance({ id: primaryInsurance.id, isPrimary: false })
              ),
              ...otherPrimaryInsurances.map((primaryInsurance: any) =>
                disableInsurance({ id: primaryInsurance.id })
              ),
            ];
          }
        }
        const editedInsurancePromise = updateInsurance({ ...values, clientId });
        await Promise.all([...otherInsuranceUpdates, editedInsurancePromise]);
        showToast({ message: "Updated Successfully!" });
        // Refresh the insurance data
        dispatch(actions.getUserInsurance(parseInt(clientId)));
        setSidePanelContent(null);
      } catch (error) {
        showToast({ message: "Error updating insurance", errorState: true });
        throw error; // Re-throw to handle in the dialog
      }
    };

    // If setting as primary and it's an edit (not adding new insurance), show confirmation dialog
    const isOnlyActiveInsurance =
      userInsurance?.filter((ins: any) => ins.active && ins.id !== values.id).length === 0;

    const activePrimaryInsurances = userInsurance?.filter((insurance: any) => {
      const expirationDate = new Date(insurance.expiration_date);
      return (
        insurance.is_primary &&
        insurance.id !== values.id &&
        insurance.active &&
        (insurance.expiration_date ? expirationDate > new Date() : true)
      );
    });
    const noActivePrimaryInsurance = activePrimaryInsurances?.length === 0;

    if (
      isSettingAsPrimary &&
      !isAddingInsurance &&
      !isOnlyActiveInsurance &&
      !noActivePrimaryInsurance
    ) {
      if (setPendingPrimaryInsuranceData && setShowSecondaryAsPrimaryDialog) {
        setPendingPrimaryInsuranceData({
          submitFunction: executeSubmit,
        });
        setShowSecondaryAsPrimaryDialog(true);
        return;
      }
    }

    // Otherwise, proceed normally
    await executeSubmit();
  };

  const isCreatingInsurance = !insurance?.id;
  const initialValues = {
    id: insurance?.id ?? "",
    isPrimary: (insurance?.is_primary || (isCreatingInsurance && !primaryActiveExists)) ?? false,
    teamMemberId: insurance?.subscriber_table_id ?? (insurance?.id ? "addNewSubscriber" : ""),
    insuranceId: insurance?.insuranceName
      ? (insuranceProvidersList?.find(({ name }: any) => name === insurance?.insuranceName)?.id ?? "")
      : insurance?.insurance_name
        ? (insuranceProvidersList?.find(({ name }: any) => name === insurance?.insurance_name)?.id ??
          "")
        : "",
    insurancePlanId: insurance?.insurance_plan_id ?? "",
    payerId: insurance?.payer_id ?? "",
    memberId: insurance?.member_id ?? "",
    groupNumber: insurance?.group_number ?? "",
    isMedicaid: insurance?.is_medicaid ?? false,
    effectiveDate: insurance?.effective_date ? new Date(insurance?.effective_date) : null,
    expirationDate: insurance?.expiration_date ? new Date(insurance?.expiration_date) : null,
    subscriberFirstName: insurance?.subscriber_first_name ?? "",
    subscriberLastName: insurance?.subscriber_last_name ?? "",
    subscriberDOB: insurance?.subscriber_dob
      ? dateWithoutTimezone(insurance?.subscriber_dob)
      : null,
    subscriberGender: insurance?.subscriber_gender ?? "",
    subscriberEmail: insurance?.subscriber_email ?? "",
    subscriberPhone: insurance?.subscriber_phone ?? "",
    subscriberRelationship: insurance?.subscriber_relationship ?? "",
    subscriberCountry: insurance?.subscriber_country || "US",
    subscriberState: insurance?.subscriber_state ?? "",
    subscriberCity: insurance?.subscriber_city ?? "",
    subscriberZip: insurance?.subscriber_zip ?? "",
    subscriberAddress: insurance?.subscriber_address ?? "",
    subscriberAddress2: insurance?.subscriber_address2 ?? "",
    subscriberTableName: insurance?.subscriber_table_name ?? null,
    subscriberTableId: insurance?.subscriber_table_id ?? null,
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({
        values,
        setFieldValue,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        errors,
      }) => {
        const isTeamMemberSelected =
          values.teamMemberId !== "" && values.teamMemberId !== "addNewSubscriber";

        const handleInsurancePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = event.target.value;
          const insurancePlanIdValue = insurancePlans?.find(({ id }: any) => id === newValue);

          setFieldValue("isMedicaid", insurancePlanIdValue?.is_medicaid || false);
          setFieldValue("insurancePlanId", newValue);

          // If the insurance plan is Medicaid, set the subscriber to the client
          if (insurancePlanIdValue?.is_medicaid && insurance?.client_id) {
            handleSubscribersAutoComplete({
              target: { value: insurance.client_id },
            } as React.ChangeEvent<HTMLInputElement>);
          }
        };

        //function handle when selected member as subscriber is chosen add the values to the subscribers fields
        const handleSubscribersAutoComplete = (event: React.ChangeEvent<HTMLInputElement>) => {
          const teamMemberId = event.target.value;

          const teamMember = teamMembersWithClientData.find(
            ({ userId }: any) => userId === Number(teamMemberId)
          );

          if (teamMember) {
            setFieldValue("subscriberFirstName", teamMember.firstName);
            setFieldValue("subscriberLastName", teamMember.lastName);
            setFieldValue("subscriberEmail", teamMember.email);
            setFieldValue("subscriberPhone", teamMember.phone);
            setFieldValue(
              "subscriberDOB",
              teamMember.dateOfBirth ? new Date(dateWithoutTimezone(teamMember.dateOfBirth)) : null
            );
            setFieldValue("subscriberGender", teamMember.gender);
            setFieldValue("subscriberRelationship", teamMember.relationship);
            setFieldValue("subscriberCountry", teamMember.country || "US");
            setFieldValue("subscriberState", teamMember.state);
            setFieldValue("subscriberCity", teamMember.city);
            setFieldValue("subscriberZip", teamMember.zip);
            setFieldValue("subscriberAddress", teamMember.address);
            setFieldValue("subscriberAddress2", teamMember.address2);
            setFieldValue("subscriberTableName", teamMember.tableName);
            setFieldValue("subscriberTableId", teamMember.userId);
          } else {
            setFieldValue("subscriberFirstName", "");
            setFieldValue("subscriberLastName", "");
            setFieldValue("subscriberEmail", "");
            setFieldValue("subscriberPhone", "");
            setFieldValue("subscriberDOB", null);
            setFieldValue("subscriberGender", null);
            setFieldValue("subscriberRelationship", null);
            setFieldValue("subscriberCountry", "US");
            setFieldValue("subscriberState", null);
            setFieldValue("subscriberCity", "");
            setFieldValue("subscriberZip", "");
            setFieldValue("subscriberAddress", "");
            setFieldValue("subscriberAddress2", "");
            setFieldValue("subscriberTableName", null);
            setFieldValue("subscriberTableId", null);
          }

          setFieldValue("teamMemberId", teamMemberId);
        };

        const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const formattedPhoneNumber = formatToPhoneNumber(e.target.value);
          setFieldValue("subscriberPhone", formattedPhoneNumber);
        };

        const handleInsuranceCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = event.target.value;
          // Check if the insuranceId has changed and reset the insurancePlanId
          if (values.insuranceId !== newValue) {
            setFieldValue("insurancePlanId", "");
            setFieldValue("insuranceId", newValue);
            setActiveInsuranceId(newValue);
          }
        };

        const handleIsPrimaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = event.target.checked;
          setFieldValue("isPrimary", newValue);

          // Verify that the effectiveDate and expirationDate are not out of date. If they are, set them to null.
          if (newValue && values?.effectiveDate && isFuture(values.effectiveDate)) {
            setFieldValue("effectiveDate", null);
          }

          if (newValue && values?.expirationDate && isPast(values.expirationDate)) {
            setFieldValue("expirationDate", null);
          }
        };

        return (
          <Stack gap={7}>
            <Alert severity="warning">
              {insurance?.has_billing_items
                ? "Insurance coverage has associated claims and therefore can not be changed with the exception of primary insurance election and expiration date."
                : "Updating client insurance fields is permitted until the first claim is billed —after that, the insurance on file can not be updated."}
            </Alert>
            <Stack gap={7}>
              <Typography variant="subtitle2" color="text.secondary">
                Coverage
              </Typography>
              <Stack gap={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isPrimary"
                      onChange={handleIsPrimaryChange}
                      checked={values.isPrimary}
                    />
                  }
                  label="Set as primary insurance"
                  disabled={insurance?.is_primary}
                />
                {!insurance?.active && (
                  <Alert severity="info">
                    Insurance coverage is eligible to be set as primary once becoming active.
                  </Alert>
                )}
              </Stack>
              <TextField
                select
                fullWidth
                name="insuranceId"
                label="Insurance company"
                value={values.insuranceId}
                onChange={handleInsuranceCompanyChange}
                onBlur={handleBlur}
                error={Boolean(touched.insuranceId && errors.insuranceId)}
                helperText={touched.insuranceId && errors.insuranceId}
                required
                disabled={insurance?.has_billing_items}
              >
                <MenuItem value={""}>Select</MenuItem>
                {insuranceProvidersList?.map((provider: any) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                name="insurancePlanId"
                label="Insurance plan"
                value={
                  // While plans are still loading, insurancePlanOptions is empty and the
                  // stored plan id wouldn't match any <MenuItem>; fall back to the
                  // placeholder until it does, matching MUI's Select contract.
                  insurancePlanOptions?.some((option: any) => option.value === values.insurancePlanId)
                    ? values.insurancePlanId
                    : ""
                }
                onChange={handleInsurancePlanChange}
                onBlur={handleBlur}
                error={Boolean(touched.insurancePlanId && errors.insurancePlanId)}
                helperText={touched.insurancePlanId && errors.insurancePlanId}
                required
                disabled={insurance?.has_billing_items}
              >
                <MenuItem disabled value={""} />
                {insurancePlanOptions?.map((provider: any) => (
                  <MenuItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                name="payerId"
                label="Payor ID"
                disabled
                value={values.payerId}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <TextField
                fullWidth
                label="Member ID"
                name="memberId"
                error={Boolean(touched.memberId && errors.memberId)}
                helperText={touched.memberId && errors.memberId}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.memberId}
                required
                disabled={insurance?.has_billing_items}
              />
              <TextField
                fullWidth
                label="Group number"
                name="groupNumber"
                error={Boolean(touched.groupNumber && errors.groupNumber)}
                helperText={touched.groupNumber && errors.groupNumber}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.groupNumber}
                disabled={insurance?.has_billing_items}
              />
              <ANDatePicker
                id="effectiveDate"
                name="effectiveDate"
                label="Effective date"
                value={values.effectiveDate}
                onChange={(value: Date) => setFieldValue("effectiveDate", value, true)}
                disableFuture={values.isPrimary ? true : false}
                format="PP"
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    error: Boolean(touched.effectiveDate && errors.effectiveDate),
                    helperText: touched.effectiveDate && errors.effectiveDate,
                    onBlur: handleBlur,
                    required: true,
                  },
                }}
                disabled={insurance?.has_billing_items}
              />
              <ANDatePicker
                id="expirationDate"
                name="expirationDate"
                label="Expiration date"
                value={values.expirationDate}
                onChange={(value: Date) => setFieldValue("expirationDate", value, true)}
                format="PP"
                sx={{ width: "100%" }}
                disabled={!values.effectiveDate}
                minDate={values.effectiveDate ? addDays(new Date(values.effectiveDate), 1) : null}
                disablePast={values.isPrimary ? true : false}
                slotProps={{
                  textField: {
                    error: Boolean(touched.expirationDate && errors.expirationDate),
                    helperText: touched.expirationDate && errors.expirationDate,
                    onBlur: handleBlur,
                  },
                }}
              />
              <FormControlLabel
                control={<Checkbox name="isMedicaid" checked={values.isMedicaid} disabled />}
                label="Medicaid insurance"
              />
            </Stack>

            <Divider />

            <Stack gap={7}>
              <Stack gap={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Insurance Subscriber
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Set the policy holder for the client&apos;s insurance. Choose a team member to
                  easily copy the necessary fields or manually add a subscriber for the Insurance
                  plan.
                </Typography>
              </Stack>

              <TextField
                select
                fullWidth
                name="teamMemberId"
                label="Team member"
                onChange={handleSubscribersAutoComplete}
                value={values.teamMemberId}
                disabled={values?.isMedicaid && values?.teamMemberId === insurance?.client_id}
              >
                <MenuItem value={""} disabled={values?.isMedicaid}>
                  Select team member
                </MenuItem>
                <MenuItem value="addNewSubscriber" disabled={values?.isMedicaid}>
                  Add New Subscriber
                </MenuItem>
                {teamMembersWithClientData?.map((member: any) => (
                  <MenuItem
                    key={member.id}
                    value={member.userId}
                    disabled={values?.isMedicaid && !member?.isMainClient}
                    sx={{
                      color: "text.primary",
                      "&.Mui-selected": {
                        color: "text.primary",
                      },
                    }}
                  >
                    {member.name}
                  </MenuItem>
                ))}
              </TextField>

              {values.teamMemberId !== "" && (
                <>
                  <Stack
                    direction="row"
                    gap={3}
                    sx={{
                      display: values.teamMemberId === "addNewSubscriber" ? "none" : "block",
                    }}
                  >
                    <EditUserInfoAlert clientId={clientId} />
                  </Stack>

                  <TextField
                    fullWidth
                    label="First name"
                    name="subscriberFirstName"
                    error={Boolean(touched.subscriberFirstName && errors.subscriberFirstName)}
                    helperText={touched.subscriberFirstName && errors.subscriberFirstName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberFirstName}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    fullWidth
                    label="Last name"
                    name="subscriberLastName"
                    error={Boolean(touched.subscriberLastName && errors.subscriberLastName)}
                    helperText={touched.subscriberLastName && errors.subscriberLastName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberLastName}
                    disabled={isTeamMemberSelected}
                  />

                  <ANDatePicker
                    id="subscriberDOB"
                    name="subscriberDOB"
                    label="Date of birth"
                    value={values.subscriberDOB}
                    onChange={(value: Date) => setFieldValue("subscriberDOB", value, true)}
                    format="PP"
                    sx={{ width: "100%" }}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    name="subscriberGender"
                    label="Gender"
                    select
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.subscriberGender}
                    fullWidth
                    disabled={isTeamMemberSelected}
                  >
                    <MenuItem disabled value={""} />
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Non-binary">Non-binary</MenuItem>
                    <MenuItem value="Gender Fluid">Gender Fluid</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    label="Email address"
                    name="subscriberEmail"
                    error={Boolean(touched.subscriberEmail && errors.subscriberEmail)}
                    helperText={touched.subscriberEmail && errors.subscriberEmail}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberEmail}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    fullWidth
                    label="Phone number"
                    name="subscriberPhone"
                    error={Boolean(touched.subscriberPhone && errors.subscriberPhone)}
                    helperText={touched.subscriberPhone && errors.subscriberPhone}
                    onBlur={handleBlur}
                    onChange={handlePhoneNumberChange}
                    value={formatToPhoneNumber(values.subscriberPhone)}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    select
                    fullWidth
                    label="Relationship to client"
                    name="subscriberRelationship"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberRelationship}
                    disabled={isTeamMemberSelected}
                  >
                    <MenuItem disabled value="">
                      relationship
                    </MenuItem>
                    {Object.entries(RELATIONSHIPS).map(([key, value]) => {
                      return (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Country"
                    name="subscriberCountry"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberCountry}
                    disabled={isTeamMemberSelected}
                  >
                    <MenuItem disabled value={""}>
                      Select
                    </MenuItem>
                    {Object.entries(COUNTRIES).map(([key, value]) => {
                      return (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="State"
                    name="subscriberState"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberState}
                    disabled={values.subscriberCountry !== "US" || isTeamMemberSelected}
                  >
                    <MenuItem disabled value={""}>
                      Select
                    </MenuItem>
                    {Object.entries(STATES).map(([key, value]) => {
                      return (
                        <MenuItem key={key} value={key}>
                          {value}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  <TextField
                    fullWidth
                    label="City"
                    name="subscriberCity"
                    error={Boolean(touched.subscriberCity && errors.subscriberCity)}
                    helperText={touched.subscriberCity && errors.subscriberCity}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberCity}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    fullWidth
                    label="ZIP code"
                    name="subscriberZip"
                    error={Boolean(touched.subscriberZip && errors.subscriberZip)}
                    helperText={touched.subscriberZip && errors.subscriberZip}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.subscriberZip}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    fullWidth
                    label="Address"
                    name="subscriberAddress"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.subscriberAddress}
                    error={touched.subscriberAddress && !!errors.subscriberAddress}
                    helperText={touched.subscriberAddress && errors.subscriberAddress}
                    disabled={isTeamMemberSelected}
                  />

                  <TextField
                    fullWidth
                    label="Address 2"
                    name="subscriberAddress2"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.subscriberAddress2}
                    error={touched.subscriberAddress2 && !!errors.subscriberAddress2}
                    helperText={touched.subscriberAddress2 && errors.subscriberAddress2}
                    disabled={isTeamMemberSelected}
                  />
                </>
              )}
            </Stack>

            <Stack gap={3}>
              <LoadingButton
                onClick={() => handleSubmit()}
                type="submit"
                loading={isMutationInsuranceLoading}
              >
                {isAddingInsurance ? "Add Insurance" : "Save changes"}
              </LoadingButton>
              <Button type="button" variant="text" onClick={() => setSidePanelContent(null)}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Formik>
  );
}
